import { useLayoutEffect } from "react";

/**
 * Syncs React drawer state with legacy Rails layout via direct DOM manipulation.
 * The panel element is rendered server-side; slideout behavior uses global classes
 * (`slideout-open`, `panel-open`) + SCSS. Layout v2 uses CSS transforms; older
 * layouts need inline styles. `useLayoutEffect` prevents paint flashes.
 *
 * Timing: 300ms transition + 50ms buffer keeps overflow/animation aligned on close.
 * `SLIDEOUT_RIGHT_INSET_PX` (50px) matches SCSS `$sidebar-slideout-pad`.
 */

const PANEL_TRANSLATE_MS = 300;
const CLOSE_DOCUMENT_DELAY_MS = PANEL_TRANSLATE_MS + 50;

const SLIDEOUT_RIGHT_INSET_PX = 50;

const getPanelEl = (): HTMLElement | null => {
  const el = document.querySelector('[data-target="mobile-sidebar.panel"]');
  return el instanceof HTMLElement ? el : null;
};

const panelUsesCssTransform = () =>
  document.body.classList.contains("is-standard-layout-v2");

const TRANSFORM_TRANSITION = `transform ${PANEL_TRANSLATE_MS}ms ease`;

const setPanelInlineTransform = (panel: HTMLElement, transform: string) => {
  panel.style.transition = TRANSFORM_TRANSITION;
  panel.style.transform = transform;
};

const clearPanelInlineTransform = (panel: HTMLElement) => {
  panel.style.transition = "";
  panel.style.transform = "";
};

const openSlideout = (
  panel: HTMLElement,
  paddingPx: number,
  shouldUseInlineTransform: boolean,
) => {
  document.documentElement.classList.add("slideout-open");
  panel.classList.add("panel-open");
  if (shouldUseInlineTransform) {
    setPanelInlineTransform(panel, `translateX(${paddingPx}px)`);
  }
};

const isSlideoutVisuallyActive = (panel: HTMLElement) =>
  document.documentElement.classList.contains("slideout-open") ||
  panel.classList.contains("panel-open");

const scheduleSlideoutClose = (
  panel: HTMLElement,
  shouldUseInlineTransform: boolean,
): (() => void) => {
  let timer: null | number = null;
  const clearTimer = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  panel.classList.remove("panel-open");
  if (shouldUseInlineTransform) {
    setPanelInlineTransform(panel, "translateX(0)");
  }

  timer = window.setTimeout(() => {
    document.documentElement.classList.remove("slideout-open");
    if (shouldUseInlineTransform) {
      clearPanelInlineTransform(panel);
    }
    timer = null;
  }, CLOSE_DOCUMENT_DELAY_MS);

  return clearTimer;
};

interface UseMobileDrawerSlideoutEffectsParams {
  isOpen: boolean;
}

export const useMobileDrawerSlideoutEffects = ({
  isOpen,
}: UseMobileDrawerSlideoutEffectsParams) => {
  useLayoutEffect(() => {
    const panel = getPanelEl();
    if (!panel) return undefined;

    panel.classList.add("slideout-panel", "slideout-panel-left");

    let cancelCloseTimer: (() => void) | undefined;

    const abortPendingClose = () => {
      cancelCloseTimer?.();
      cancelCloseTimer = undefined;
    };

    abortPendingClose();

    const shouldUseInlineTransform = !panelUsesCssTransform();
    const paddingPx = window.outerWidth - SLIDEOUT_RIGHT_INSET_PX;

    if (isOpen) {
      openSlideout(panel, paddingPx, shouldUseInlineTransform);
    } else if (isSlideoutVisuallyActive(panel)) {
      cancelCloseTimer = scheduleSlideoutClose(panel, shouldUseInlineTransform);
    }

    return abortPendingClose;
  }, [isOpen]);
};
